import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { findLeftMenuBreadcrumb } from "./leftMenuBreadcrumbs";

const Breadcrumb = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const appRouteLabels = {
    "/onboarding-hexagon": "Quick Links",
    "/ManageWorkflowPerformer": "Manage Workflow Role Users",
    "/ManagePartnerPerformer": "Manage Users",
    "/ReviewTask": "Review Workflow Task",
    "/CandidateDetails": "Candidate Details",
    "/Profile": "My Profile",
    "/PrintTemplate": "Print Template",
    "/CandidateInvoice": "Candidate Invoice Detail",
    "/MngBprmAccess": "WorkFinity BPRM",
    "/AccessDdrms": "WorkFinity DDRMS",
    "/ApplicationSummary": "Application Summary",
    "/DownloadDocLink": "Download Document",
    "/ViewFormLink": "View Form",
  };

  const detailRouteParents = {
    "/ManageWorkflowPerformer": [
      { label: "System Admin" },
      { label: "Authorized Users" },
      { label: "Main Users", url: "/WorkflowUser" },
    ],
    "/ManagePartnerPerformer": [
      { label: "System Admin" },
      { label: "Manage Entities" },
    ],
    "/MngBprmAccess": [{ label: "System Admin" }],
    "/AccessDdrms": [{ label: "System Admin" }],
    "/ReviewTask": [{ label: "Onboarding" }],
    "/CandidateDetails": [{ label: "Onboarding" }],
    "/CandidateInvoice": [{ label: "Invoices" }],
  };

  const paramLabels = {
    application: "Applications",
    mail: "Email",
    partner: "Partners",
    text: "Text",
  };

  const toTitleCase = (value = "") =>
    value
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const getBreadcrumbIcon = (label = "") => {
    const l = label.toLowerCase();
    if (l.includes("quick links")) {
      return <i className="fa fa-home" style={{ color: "#2563eb", fontSize: "14px" }} />;
    }
    if (l === "system admin" || l === "system administration") {
      return <i className="fa fa-cogs" style={{ color: "#475569", fontSize: "13px" }} />;
    }
    if (l.includes("authorized users")) {
      return <i className="fa fa-user" style={{ color: "#475569", fontSize: "13px" }} />;
    }
    if (l.includes("manage entities")) {
      return <i className="fa fa-building" style={{ color: "#475569", fontSize: "13px" }} />;
    }
    if (l.includes("subcontractor")) {
      return <i className="fa fa-handshake-o" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    if (l.includes("recruiter")) {
      return <i className="fa fa-users" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    if (l.includes("candidate")) {
      return <i className="fa fa-graduation-cap" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    if (l === "onboarding") {
      return <i className="fa fa-file-text" style={{ color: "#475569", fontSize: "13px" }} />;
    }
    if (l.includes("main users") || l.includes("workflow role users")) {
      return <i className="fa fa-user-circle-o" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    if (l.includes("user")) {
      return <i className="fa fa-user" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    if (l.includes("invoice") || l.includes("payment")) {
      return <i className="fa fa-money" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    if (l.includes("timelog") || l.includes("time log")) {
      return <i className="fa fa-clock-o" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    if (l.includes("dashboard")) {
      return <i className="fa fa-tachometer" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    if (l.includes("security") || l.includes("log") || l.includes("monitoring")) {
      return <i className="fa fa-shield" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    if (l.includes("email") || l.includes("mail") || l.includes("communication")) {
      return <i className="fa fa-envelope-o" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    if (l.includes("report") || l.includes("analytic")) {
      return <i className="fa fa-bar-chart" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    if (l.includes("task") || l.includes("review")) {
      return <i className="fa fa-check-square-o" style={{ color: "#2563eb", fontSize: "13px" }} />;
    }
    return <i className="fa fa-folder-open-o" style={{ color: "#64748b", fontSize: "13px" }} />;
  };

  const getCurrentRouteKey = (pathname) => {
    if (appRouteLabels[pathname]) return pathname;
    return Object.keys(appRouteLabels)
      .filter((route) => route !== "/" && pathname.startsWith(`${route}/`))
      .sort((a, b) => b.length - a.length)[0];
  };

  const getStateCrumbs = () => {
    const state = location?.state;
    const crumbs = [];

    if (state?.partner && state?.url) {
      const url = state.url.startsWith("/") ? state.url : `/${state.url}`;
      crumbs.push({ type: "link", label: state.partner, url });
    }

    if (state?.obj?.label) {
      crumbs.push({ type: "text", label: state.obj.label });
    } else if (state?.rolename) {
      crumbs.push({ type: "text", label: state.rolename });
    }

    return crumbs;
  };

  const leftMenuMatch = findLeftMenuBreadcrumb(location.pathname);
  const routeKey = getCurrentRouteKey(location.pathname);
  const activeLabel =
    leftMenuMatch?.route?.label ||
    appRouteLabels[routeKey] ||
    toTitleCase(location.pathname.split("/").filter(Boolean).pop() || "Quick Links");
  const pathParts = location.pathname.split("/").filter(Boolean);
  const matchedRoutePath = leftMenuMatch?.route?.path || routeKey;
  const extraPathParts = matchedRoutePath
    ? pathParts.slice(matchedRoutePath.split("/").filter(Boolean).length)
    : routeKey
      ? pathParts.slice(routeKey.split("/").filter(Boolean).length)
      : pathParts.slice(1);

  // Build breadcrumb items array sequentially
  const items = [
    {
      type: "link",
      label: "Quick Links",
      url: "/onboarding-hexagon",
    },
  ];

  if (leftMenuMatch) {
    [
      { label: leftMenuMatch.section },
      leftMenuMatch.group && { label: leftMenuMatch.group },
    ].filter(Boolean).forEach((crumb) => {
      items.push({
        type: "text",
        label: crumb.label,
      });
    });
  } else if (routeKey !== "/") {
    (detailRouteParents[routeKey] || []).forEach((crumb) => {
      items.push({
        type: crumb.url ? "link" : "text",
        label: crumb.label,
        url: crumb.url,
      });
    });
  }

  getStateCrumbs().forEach((crumb) => {
    const duplicate = items.some((item) => item.label === crumb.label);
    if (!duplicate) items.push(crumb);
  });

  extraPathParts.forEach((part) => {
    const label = paramLabels[part] || toTitleCase(decodeURIComponent(part));
    const duplicate = items.some((item) => item.label === label);
    if (label && label !== activeLabel && !duplicate) {
      items.push({
        type: "text",
        label,
      });
    }
  });

  if (location.pathname === "/onboarding-hexagon") {
    items[0].type = "active";
  } else if (activeLabel && routeKey !== "/") {
    items.push({
      type: "active",
      label: activeLabel,
    });
  }

  return (
    <div className="modern-breadcrumb-wrapper">
      <style>{`
        @keyframes breadcrumbFadeIn {
          from {
            opacity: 0;
            transform: translateY(6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes activePulse {
          0% {
            box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.15);
          }
          70% {
            box-shadow: 0 0 0 5px rgba(37, 99, 235, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
          }
        }

        .modern-breadcrumb-wrapper {
          animation: breadcrumbFadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
          width: 100%;
        }

        .modern-breadcrumb-container {
          background: #ffffff !important;
          backdrop-filter: blur(12px) !important;
          -webkit-backdrop-filter: blur(12px) !important;
          border: 1px solid rgba(226, 232, 240, 0.8) !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.03), 0 2px 4px -1px rgba(0, 0, 0, 0.02) !important;
          border-radius: 14px !important;
          padding: 10px 18px !important;
          margin-top: 14px !important;
          margin-bottom: 8px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          flex-wrap: wrap !important;
          gap: 12px !important;
          transition: all 0.3s ease !important;
        }

        .modern-breadcrumb-container:hover {
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03) !important;
          border-color: rgba(203, 213, 225, 1) !important;
          background: #ffffff !important;
        }

        .modern-breadcrumb-nav {
          display: flex !important;
          align-items: center !important;
        }

        .modern-breadcrumb-list {
          display: flex !important;
          align-items: center !important;
          flex-wrap: wrap !important;
          list-style: none !important;
          margin: 0 !important;
          padding: 0 !important;
          gap: 4px !important;
        }

        .modern-breadcrumb-item {
          display: flex !important;
          align-items: center !important;
        }

        .modern-breadcrumb-item a, 
        .modern-breadcrumb-item button {
          display: flex !important;
          align-items: center !important;
          gap: 6px !important;
          color: #475569 !important;
          font-size: 13.5px !important;
          font-weight: 500 !important;
          text-decoration: none !important;
          background: transparent !important;
          border: none !important;
          padding: 6px 10px !important;
          border-radius: 8px !important;
          cursor: pointer !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
          outline: none !important;
          line-height: 1.2 !important;
        }

        .modern-breadcrumb-item a:hover, 
        .modern-breadcrumb-item button:hover {
          color: #2563eb !important;
          background: rgba(37, 99, 235, 0.06) !important;
          transform: translateY(-1px) !important;
        }

        .modern-breadcrumb-item a:active, 
        .modern-breadcrumb-item button:active {
          transform: translateY(0) !important;
        }

        .modern-breadcrumb-item span.text-black {
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          color: #1e293b !important;
          font-size: 13.5px !important;
          font-weight: 500 !important;
          padding: 6px 10px !important;
        }

        .modern-breadcrumb-separator {
          color: #94a3b8 !important;
          font-size: 11px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 16px !important;
          user-select: none !important;
          opacity: 0.8;
        }

        .modern-breadcrumb-active {
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.08), rgba(37, 99, 235, 0.12)) !important;
          border: 1px solid rgba(37, 99, 235, 0.15) !important;
          color: #1d4ed8 !important;
          padding: 6px 12px !important;
          border-radius: 8px !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          letter-spacing: 0.1px !important;
          animation: activePulse 2.5s infinite;
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
        }

        .modern-breadcrumb-actions {
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
        }

        .breadcrumb-back-btn {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
          background: #ffffff !important;
          border: 1px solid #e2e8f0 !important;
          padding: 6px 14px !important;
          border-radius: 8px !important;
          font-size: 12.5px !important;
          font-weight: 600 !important;
          color: #475569 !important;
          cursor: pointer !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
          outline: none !important;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.02) !important;
          min-height: 38px !important;
        }

        .breadcrumb-back-btn:hover {
          background: #f8fafc !important;
          color: #0f172a !important;
          border-color: #cbd5e1 !important;
          transform: translateX(-2px) !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
        }

        .breadcrumb-back-btn:active {
          transform: translateX(-1px) !important;
        }

        .breadcrumb-back-btn i {
          font-size: 11px !important;
          transition: transform 0.2s ease !important;
        }

        .breadcrumb-back-btn:hover i {
          transform: translateX(-1px) !important;
        }
      `}</style>

      <div className="modern-breadcrumb-container">
        <nav aria-label="breadcrumb" className="modern-breadcrumb-nav">
          <ol className="modern-breadcrumb-list">
            {items.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <span className="modern-breadcrumb-separator">
                    <i className="fa fa-angle-right" />
                  </span>
                )}
                <li className="modern-breadcrumb-item">
                  {item.type === "link" && (
                    <Link to={item.url} aria-label={item.label} className="d-inline-flex align-items-center">
                      {item.icon || getBreadcrumbIcon(item.label)}
                      {item.label}
                    </Link>
                  )}
                  {item.type === "text" && (
                    <span className="text-black d-inline-flex align-items-center">
                      {item.icon || getBreadcrumbIcon(item.label)}
                      {item.label}
                    </span>
                  )}
                  {item.type === "active" && (
                    <span className="modern-breadcrumb-active d-inline-flex align-items-center">
                      {item.icon || getBreadcrumbIcon(item.label)}
                      {item.label}
                    </span>
                  )}
                </li>
              </React.Fragment>
            ))}
          </ol>
        </nav>

        <div className="modern-breadcrumb-actions">
          <button
            onClick={() => navigate(-1)}
            className="breadcrumb-back-btn"
            title="Go to previous page"
          >
            <i className="fa fa-arrow-left" /> Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default Breadcrumb;
